import { type PropsWithChildren, type ReactElement, useState } from 'react'
import { createPortal } from 'react-dom'

import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Tab from '@mui/material/Tab'
import MuiTabs from '@mui/material/Tabs'

import { useScrollToId } from '@hooks/useScrollToId'
import { truncateString } from '@util/string-util'

import { usePageInfo } from '../hooks/usePageInfo'

import { type BreadcrumbTrail, Breadcrumbs } from './Breadcrumbs'

export { Page }

type Tab = {
  label: string
  id: string
  icon: ReactElement
}

type Props = PropsWithChildren<{
  title: string[]
  breadcrumbs: BreadcrumbTrail
  tabs?: Tab[]
  gap?: number
  fillScreen?: boolean
}>

const SCROLL_MARGIN = 136

const Page = ({
  title,
  breadcrumbs,
  children,
  tabs = [],
  gap = 0,
  fillScreen = false,
}: Props) => {
  const { breadcrumbsSlot } = usePageInfo()
  const [value, setValue] = useState(0)
  const { scrollToId } = useScrollToId()

  const handleTabChange = (newValue: number) => {
    const { id } = tabs[newValue]

    if (id) {
      setValue(newValue)
      scrollToId(id, SCROLL_MARGIN)
    }
  }

  const pageTitle = [...title.map((part) => truncateString(part)), 'CAPO'].join(
    ' | ',
  )

  return (
    <>
      <title>{pageTitle}</title>

      {breadcrumbsSlot &&
        createPortal(
          <Breadcrumbs breadcrumbs={breadcrumbs} />,
          breadcrumbsSlot,
        )}

      {tabs.length > 0 && (
        <Box
          sx={{
            zIndex: 1100,
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: 'divider',
            position: 'sticky',
            top: '64px',
            backgroundColor: '#f0f0f0',

            '.MuiTabs-scrollButtons.Mui-disabled': {
              opacity: 0.3,
            },
          }}
        >
          <Container maxWidth="xl">
            <MuiTabs
              variant="scrollable"
              value={value}
              onChange={(_, value) => handleTabChange(value)}
              orientation="horizontal"
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.label}
                  label={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                  sx={{
                    minHeight: 48,
                    padding: '8px 16px',
                    justifyContent: 'flex-start',

                    '& .MuiSvgIcon-root': {
                      marginRight: 1,
                      fontSize: '1.25rem',
                    },
                  }}
                />
              ))}
            </MuiTabs>
          </Container>
        </Box>
      )}

      {fillScreen ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap,
            paddingTop: 4,
            paddingBottom: 8,
            paddingLeft: 3,
            paddingRight: 3,
          }}
        >
          {children}
        </Box>
      ) : (
        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap,
            paddingTop: 8,
            paddingBottom: 8,
          }}
        >
          {children}
        </Container>
      )}
    </>
  )
}
